import { supabase } from "./supabase";
import { leaderboardFromAnswers } from "../shared/leaderboard";
import type { AnswerRow, RankedLeaderboardEntry } from "../shared/types";
import type { SubjectStat } from "../shared/superlatives";

// Reads used across screens. Everything here goes through the RLS-guarded browser
// client, so a member can only ever read what the privacy model permits.

export interface Member {
  user_id: string;
  display_name: string;
  role: "member" | "commissioner";
  push_status: string;
}

export interface MyLeague {
  id: string;
  name: string;
  role: "member" | "commissioner";
}

// Every league this person belongs to. One address is expected to run several at
// once (cousins, college, old coworkers), so a single "first league" was always
// the wrong shape: it silently picked whichever row came back first.
export async function getMyLeagues(userId: string): Promise<MyLeague[]> {
  const { data } = await supabase
    .from("league_memberships")
    .select("league_id, role, leagues(name)")
    .eq("user_id", userId)
    .eq("invite_status", "joined");
  return (data ?? [])
    .map((m: any) => ({
      id: m.league_id as string,
      name: (m.leagues?.name as string) || "League",
      role: m.role as "member" | "commissioner",
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

const LAST_LEAGUE_KEY = "last_league_id";

// Which league to open when no specific one was asked for: the one they used
// last, otherwise the only one they have.
export function rememberLeague(leagueId: string) {
  try { localStorage.setItem(LAST_LEAGUE_KEY, leagueId); } catch { /* private mode */ }
}

export function pickLeague(leagues: MyLeague[]): string | null {
  if (leagues.length === 0) return null;
  let last: string | null = null;
  try { last = localStorage.getItem(LAST_LEAGUE_KEY); } catch { /* private mode */ }
  if (last && leagues.some((l) => l.id === last)) return last;
  return leagues[0].id;
}

export async function getFirstLeagueId(userId: string): Promise<string | null> {
  return pickLeague(await getMyLeagues(userId));
}

export async function getLeagueState(leagueId: string, userId: string) {
  const { data: league } = await supabase.from("leagues").select("*").eq("id", leagueId).single();
  const { data: season } = await supabase.from("seasons").select("*").eq("league_id", leagueId).maybeSingle();
  const { data: memberships } = await supabase
    .from("league_memberships")
    .select("user_id, role, push_status, users(display_name)")
    .eq("league_id", leagueId)
    .eq("invite_status", "joined");
  const members: Member[] = (memberships ?? []).map((m: any) => ({
    user_id: m.user_id,
    display_name: m.users?.display_name ?? "Friend",
    role: m.role,
    push_status: m.push_status,
  }));
  const me = members.find((m) => m.user_id === userId) ?? null;
  return { league, season, members, me };
}

// Every live drop, not just the newest. Showing only one meant a second live
// drop was unreachable from home, and tapping through landed the player in a
// drop they had already finished.
export async function getLiveDrops(seasonId: string) {
  const { data } = await supabase
    .from("member_drops")
    .select("*")
    .eq("season_id", seasonId)
    .eq("status", "live")
    .order("sequence_number", { ascending: true });
  return data ?? [];
}

// Full leaderboard, derived from revealed-drop answers (RLS exposes those to all
// members) plus the caller's own answers. Names resolved from memberships.
export async function getLeaderboard(
  seasonId: string,
  members: Member[]
): Promise<(RankedLeaderboardEntry & { name: string })[]> {
  // Gather revealed drops for this season, then their answers.
  const { data: drops } = await supabase
    .from("member_drops")
    .select("id")
    .eq("season_id", seasonId)
    .eq("status", "revealed");
  const dropIds = (drops ?? []).map((d) => d.id);
  if (dropIds.length === 0) {
    return members.map((m, i) => ({
      user_id: m.user_id, name: m.display_name, points: 0, correct_count: 0,
      attempted_count: 0, avg_correct_response_ms: null, total_correct_response_ms: 0, rank: 1 + i * 0,
    })).map((e) => ({ ...e, rank: 1 }));
  }
  const { data: dqs } = await supabase.from("drop_questions").select("id").in("drop_id", dropIds);
  const dqIds = (dqs ?? []).map((d) => d.id);
  const { data: answers } = await supabase
    .from("answers")
    .select("user_id, drop_question_id, result, points_awarded, response_duration_ms")
    .in("drop_question_id", dqIds);

  const rows: AnswerRow[] = (answers ?? []).map((a: any) => ({
    user_id: a.user_id,
    drop_question_id: a.drop_question_id,
    subject_user_id: "",
    result: a.result,
    points_awarded: a.points_awarded,
    response_duration_ms: a.response_duration_ms,
  }));
  const nameOf = new Map(members.map((m) => [m.user_id, m.display_name]));
  const ranked = leaderboardFromAnswers(rows);
  // Ensure every member appears even with no answers yet.
  for (const m of members) {
    if (!ranked.find((r) => r.user_id === m.user_id)) {
      ranked.push({
        user_id: m.user_id, points: 0, correct_count: 0, attempted_count: 0,
        avg_correct_response_ms: null, total_correct_response_ms: 0, rank: ranked.length + 1,
      });
    }
  }
  return ranked.map((r) => ({ ...r, name: nameOf.get(r.user_id) ?? "Friend" }));
}

// Standings plus how far each person moved in the drop that just revealed.
// Without this the leaderboard is a static list and climbing it is invisible,
// which is the part that actually keeps people coming back.
export async function getLeaderboardWithMovement(
  seasonId: string,
  members: Member[]
): Promise<(RankedLeaderboardEntry & { name: string; movement: number | null })[]> {
  const { data: drops } = await supabase
    .from("member_drops")
    .select("id, revealed_at")
    .eq("season_id", seasonId)
    .eq("status", "revealed")
    .order("revealed_at", { ascending: true });

  const current = await getLeaderboard(seasonId, members);
  const revealed = drops ?? [];
  // One revealed drop means there is no "before" to compare against yet.
  if (revealed.length < 2) return current.map((e) => ({ ...e, movement: null }));

  const priorIds = revealed.slice(0, -1).map((d) => d.id);
  const { data: dqs } = await supabase.from("drop_questions").select("id").in("drop_id", priorIds);
  const dqIds = (dqs ?? []).map((d) => d.id);
  const { data: answers } = await supabase
    .from("answers")
    .select("user_id, drop_question_id, result, points_awarded, response_duration_ms")
    .in("drop_question_id", dqIds);

  const rows: AnswerRow[] = (answers ?? []).map((a: any) => ({
    user_id: a.user_id,
    drop_question_id: a.drop_question_id,
    subject_user_id: "",
    result: a.result,
    points_awarded: a.points_awarded,
    response_duration_ms: a.response_duration_ms,
  }));
  const before = leaderboardFromAnswers(rows);
  const rankBefore = new Map(before.map((r) => [r.user_id, r.rank]));

  return current.map((e) => {
    const was = rankBefore.get(e.user_id);
    // A positive number means they climbed: rank 4 to rank 2 is +2.
    return { ...e, movement: was == null ? null : was - e.rank };
  });
}

// Pulls a season's worth, not a hard five. Home shows a few and lets people open
// the rest, so the number fetched is no longer the number visible.
export async function getRecentReveals(seasonId: string, maxItems = 30) {
  const { data } = await supabase.rpc("recent_reveals", { target_season: seasonId, max_items: maxItems });
  return (data ?? []) as { drop_question_id: string; subject_user_id: string; reveal_context: string; revealed_at: string }[];
}

export async function myApprovedCount(seasonId: string, userId: string): Promise<number> {
  const { count } = await supabase
    .from("question_candidates")
    .select("id", { count: "exact", head: true })
    .eq("season_id", seasonId)
    .eq("owner_user_id", userId)
    .eq("status", "approved");
  return count ?? 0;
}

export function daysRemaining(endsOn: string): number {
  const end = new Date(endsOn + "T23:59:59").getTime();
  return Math.max(0, Math.ceil((end - Date.now()) / 86400000));
}

// Week of the season, 1-based. Seasons are no longer fixed at 14 days, so this
// counts real elapsed weeks instead of capping at 2.
export function weekNumber(startsOn: string): number {
  const start = new Date(startsOn + "T00:00:00").getTime();
  const elapsed = Date.now() - start;
  if (elapsed < 0) return 1;
  return Math.floor(elapsed / (7 * 86400000)) + 1;
}

// How well the league knew each person this season, for the finale superlatives.
//
// Read-only, and it touches nothing the app does not already read elsewhere: the
// Reveal screen makes these exact queries for a single drop. RLS only exposes
// drop_questions and other people's answers once a drop is revealed, so this can
// never surface unplayed content.
export async function getSubjectStats(seasonId: string): Promise<SubjectStat[]> {
  const { data: drops } = await supabase
    .from("member_drops")
    .select("id")
    .eq("season_id", seasonId)
    .eq("status", "revealed");
  const dropIds = (drops ?? []).map((d) => d.id);
  if (dropIds.length === 0) return [];

  // Trivia has no subject, so it says nothing about how well anyone is known.
  const { data: dqs } = await supabase
    .from("drop_questions")
    .select("id, subject_user_id, kind")
    .in("drop_id", dropIds);
  const homie = (dqs ?? []).filter((d) => d.kind !== "trivia" && d.subject_user_id);
  if (homie.length === 0) return [];

  const subjectOf = new Map(homie.map((d) => [d.id as string, d.subject_user_id as string]));
  const { data: answers } = await supabase
    .from("answers")
    .select("drop_question_id, result")
    .in("drop_question_id", [...subjectOf.keys()]);

  const stats = new Map<string, SubjectStat>();
  const ensure = (id: string): SubjectStat => {
    let e = stats.get(id);
    if (!e) { e = { subject_user_id: id, asked: 0, attempts: 0, correct: 0 }; stats.set(id, e); }
    return e;
  };
  for (const id of subjectOf.values()) ensure(id).asked += 1;
  for (const a of answers ?? []) {
    const subject = subjectOf.get(a.drop_question_id);
    if (!subject) continue;
    // A sit-out is the subject themselves, and says nothing about being known.
    if (a.result === "subject_sitout") continue;
    const e = ensure(subject);
    e.attempts += 1;
    if (a.result === "correct") e.correct += 1;
  }
  return [...stats.values()];
}
